import React, { useState, useEffect, useCallback } from "react";
import style from "./style.module.css";
import TruncatedText from "../../Components/TruncatedText";
import { usePermissions } from "../../hooks/usePermissions";
import { useCookies } from "react-cookie";
import { authenticatedFetch } from "../../utils/api";
import { API_BASE, resolveImageUrl } from "../../config/api";
import Pagination from "../../Components/Pagination";
import useSnackbar from "../../context/Snackbar/useSnackbar";
import { parseApiError } from "../../utils/parseApiError";

const PAGE_SIZE = 25;

interface Category {
  category_id: number;
  shop_id: number;
  category_name: string;
  category_description: string;
  category_image: string;
  category_image_url?: string;
  created_at: string;
  updated_at: string;
}

interface EditCategory {
  category_id?: number;
  shop_id?: number;
  category_name: string;
  category_description: string;
  category_image: string;
  category_image_url?: string;
}

interface Shop {
  shop_id: number;
  shop_name: string;
}

const CategoryPage = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [shops, setShops] = useState<Shop[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [selectedCategories, setSelectedCategories] = useState<number[]>([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editCategory, setEditCategory] = useState<EditCategory>({
    category_name: "",
    category_description: "",
    category_image: ""
  });
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const permissions = usePermissions();
  const [cookies] = useCookies();
  const snack = useSnackbar();

  useEffect(() => {
    fetchShops();
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearchTerm(searchTerm), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm]);

  const fetchCategories = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page: page.toString(), limit: PAGE_SIZE.toString() });
      if (debouncedSearchTerm) params.set("search", debouncedSearchTerm);
      const response = await authenticatedFetch(`${API_BASE}/category?${params.toString()}`, {
        method: 'GET'
      }, cookies.auth);

      if (response.ok) {
        const data = await response.json();
        setCategories(Array.isArray(data?.data) ? data.data : []);
        setTotalPages(data?.totalPages || 1);
        setTotalItems(data?.total || 0);
      } else {
        console.error("Failed to fetch categories. Status:", response.status);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  }, [cookies.auth, page, debouncedSearchTerm]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const fetchShops = useCallback(async () => {
    try {
      const response = await authenticatedFetch(`${API_BASE}/shop?page=1&limit=100`, {
        method: 'GET'
      }, cookies.auth);

      if (response.ok) {
        const data = await response.json();
        setShops(Array.isArray(data?.data) ? data.data : []);
      } else {
        console.error("Failed to fetch shops. Status:", response.status);
      }
    } catch (error) {
      console.error("Error fetching shops:", error);
    }
  }, [cookies.auth]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setImageFile(e.target.files[0]);
    }
  };

  const convertFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = error => reject(error);
    });
  };

  const handleAdd = () => {
    setEditCategory({
      category_name: "",
      category_description: "",
      category_image: ""
    });
    setImageFile(null);
    setShowAddModal(true);
  };

  const handleUpdate = () => {
    if (selectedCategories.length === 0) {
      alert("Please select at least one category to update");
      return;
    }
    
    const categoryToEdit = categories.find(category => category.category_id === selectedCategories[0]);
    if (categoryToEdit) {
      setEditCategory({
        category_id: categoryToEdit.category_id,
        shop_id: categoryToEdit.shop_id,
        category_name: categoryToEdit.category_name,
        category_description: categoryToEdit.category_description,
        category_image: categoryToEdit.category_image || "",
        category_image_url: categoryToEdit.category_image_url
      });
      setImageFile(null);
      setShowEditModal(true);
    }
  };

  const handleDelete = () => {
    if (selectedCategories.length === 0) {
      alert("Please select at least one category to delete");
      return;
    }
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    try {
      setIsLoading(true);
      for (const categoryId of selectedCategories) {
        const response = await authenticatedFetch(`${API_BASE}/category/${categoryId}`, {
          method: "DELETE"
        }, cookies.auth);
        
        if (!response.ok) {
          console.error(`Failed to delete category ${categoryId}. Status:`, response.status);
        }
      }
      setSelectedCategories([]);
      setShowDeleteConfirm(false);
      await fetchCategories();
    } catch (error) {
      console.error("Error deleting categories:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async (isEdit: boolean) => {
    try {
      setIsLoading(true);
      let imageBase64: string | null = null;
      
      if (imageFile) {
        try {
          imageBase64 = await convertFileToBase64(imageFile!);
        } catch (error) {
          console.error('Error converting image to base64:', error);
          return;
        }
      }

      // Validate required fields - ensure we have valid string values
      const categoryName = String(editCategory?.category_name || "").trim();
      if (!categoryName) {
        snack.onResponse({ message: "Category name is required", status: 400 });
        setIsLoading(false);
        return;
      }

      if (!editCategory.shop_id || editCategory.shop_id === undefined) {
        snack.onResponse({ message: "Please select a shop", status: 400 });
        setIsLoading(false);
        return;
      }

      const shopId = parseInt(String(editCategory.shop_id));

      if (isNaN(shopId)) {
        snack.onResponse({ message: "Invalid shop ID", status: 400 });
        setIsLoading(false);
        return;
      }

      // Ensure description is always a string, never undefined
      const categoryDescription = String(editCategory?.category_description || "").trim();

      const payload: any = {
        name: categoryName,
        description: categoryDescription,
        shop_id: shopId
      };

      // Only include image if it has a value (base64 string or existing image)
      const imageValue = imageBase64 || editCategory?.category_image;
      if (imageValue && String(imageValue).trim()) {
        payload.image = String(imageValue).trim();
      }

      console.log("Category payload being sent:", payload);

      const url = isEdit 
        ? `${API_BASE}/category/${editCategory.category_id}`
        : `${API_BASE}/category`;
      
      const method = isEdit ? "PUT" : "POST";

      const response = await authenticatedFetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      }, cookies.auth);

      if (response.ok) {
        const responseData = await response.json();
        console.log("Success response:", responseData);

        setShowEditModal(false);
        setShowAddModal(false);
        setEditCategory({
          category_name: "",
          category_description: "",
          category_image: ""
        });
        setImageFile(null);
        setSelectedCategories([]);
        await fetchCategories();
      } else {
        snack.onResponse({ message: await parseApiError(response, "Failed to save category"), status: response.status });
      }
    } catch (error) {
      console.error("Error saving category:", error);
      snack.onResponse({ message: error instanceof Error ? error.message : "Failed to save category", status: 400 });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedCategories(categories.map(category => category.category_id));
    } else {
      setSelectedCategories([]);
    }
  };

  const handleSelectCategory = (categoryId: number, checked: boolean) => {
    if (checked) {
      setSelectedCategories([categoryId]);
    } else {
      setSelectedCategories(selectedCategories.filter(id => id !== categoryId));
    }
  };

  const filteredCategories = categories;

  const getShopName = (shopId: number | null | undefined) => {
    if (!shopId) return "Unknown";
    const shop = shops.find(s => s.shop_id === shopId);
    return shop ? shop.shop_name : "Unknown";
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return "Invalid Date";
    }
  };

  return (
    <div className={style.wrapper}>
      {/* Header Section */}
      <div className={style.header}>
        <h1 className={style.title}>Category</h1>
        <div className={style.searchSection}>
          <input
            type="text"
            placeholder="Search by category name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={style.searchInput}
          />
          {permissions.canAdd && (
            <div className={style.actionButtons}>
              <button className={style.actionBtn} onClick={handleAdd}>
                Add
              </button>
              <button className={style.actionBtn} onClick={handleUpdate}>
                Update
              </button>
              <button className={style.actionBtnDanger} onClick={handleDelete}>
                Delete Category
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Table Section */}
      <div className={style.tableContainer}>
        <table className={style.dataTable}>
          <thead>
            <tr>
              {permissions.canEdit && (
                <th>
                  <input
                    type="checkbox"
                    checked={selectedCategories.length === categories.length && categories.length > 0}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className={style.checkbox}
                  />
                </th>
              )}
              <th>Category ID</th>
              <th>Shop</th>
              <th>Category Name</th>
              <th>Description</th>
              <th>Image</th>
              <th>Created At</th>
              <th>Updated At</th>
            </tr>
          </thead>
          <tbody>
            {filteredCategories.map((category) => (
              <tr key={category.category_id} className={style.tableRow}>
                {permissions.canEdit && (
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedCategories.includes(category.category_id)}
                      onChange={(e) => handleSelectCategory(category.category_id, e.target.checked)}
                      className={style.checkbox}
                    />
                  </td>
                )}
                <td>{category.category_id}</td>
                <td>{getShopName(category.shop_id)}</td>
                <td>{category.category_name}</td>
                <td className={style.descriptionCell}>
                  <TruncatedText text={category.category_description} maxLength={50} />
                </td>
                <td>
                  <img
                    src={category.category_image_url
                      ? resolveImageUrl(category.category_image_url)
                      : 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjYwIiBoZWlnaHQ9IjYwIiBmaWxsPSIjRjVGNUY1Ii8+CjxwYXRoIGQ9Ik0zMCAzNUMzMi43NjE0IDM1IDM1IDMyLjc2MTQgMzUgMzBDMzUgMjcuMjM4NiAzMi43NjE0IDI1IDMwIDI1QzI3LjIzODYgMjUgMjUgMjcuMjM4NiAyNSAzMEMyNSAzMi43NjE0IDI3LjIzODYgMzUgMzAgMzVaIiBmaWxsPSIjQ0NDQ0NDIi8+CjxwYXRoIGQ9Ik0zMCA0MEMzMi43NjE0IDQwIDM1IDM3Ljc2MTQgMzUgMzVDMzUgMzIuMjM4NiAzMi43NjE0IDMwIDMwIDMwQzI3LjIzODYgMzAgMjUgMzIuMjM4NiAyNSAzNUMyNSAzNy43MjE0IDI3LjIzODYgNDAgMzAgNDBaIiBmaWxsPSIjQ0NDQ0NDIi8+Cjwvc3ZnPgo='
                    }
                    alt={category.category_name}
                    className={style.categoryImage}
                    onError={(e) => {
                      e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjYwIiBoZWlnaHQ9IjYwIiBmaWxsPSIjRjVGNUY1Ii8+CjxwYXRoIGQ9Ik0zMCAzNUMzMi43NjE0IDM1IDM1IDMyLjc2MTQgMzUgMzBDMzUgMjcuMjM4NiAzMi43NjE0IDI1IDMwIDI1QzI3LjIzODYgMjUgMjUgMjcuMjM4NiAyNSAzMEMyNSAzMi43NjE0IDI3LjIzODYgMzUgMzAgMzVaIiBmaWxsPSIjQ0NDQ0NDIi8+CjxwYXRoIGQ9Ik0zMCA0MEMzMi43NjE0IDQwIDM1IDM3Ljc2MTQgMzUgMzVDMzUgMzIuMjM4NiAzMi43NjE0IDMwIDMwIDMwQzI3LjIzODYgMzAgMjUgMzIuMjM4NiAyNSAzNUMyNSAzNy43MjE0IDI3LjIzODYgNDAgMzAgNDBaIiBmaWxsPSIjQ0NDQ0NDIi8+Cjwvc3ZnPgo='
                    }}
                  />
                </td>
                <td>{formatDate(category.created_at)}</td>
                <td>{formatDate(category.updated_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination page={page} totalPages={totalPages} onPageChange={setPage} totalItems={totalItems} />

      {/* Add/Edit Modal */}
      {(showAddModal || showEditModal) && (
        <div className={style.modalOverlay}>
          <div className={style.modal}>
            <h2>{showAddModal ? "Add New Category" : "Update Category"}</h2>
            <div className={style.modalContent}>
              <input
                type="text"
                placeholder="Category Name *"
                value={editCategory.category_name}
                onChange={(e) => setEditCategory({...editCategory, category_name: e.target.value})}
                className={style.modalInput}
                required
              />
              <select
                value={editCategory.shop_id || ""}
                onChange={(e) => {
                  const shopId = parseInt(e.target.value);
                  const shop = shops.find(s => s.shop_id === shopId);
                  if (shop) {
                    setEditCategory({...editCategory, shop_id: shopId});
                  }
                }}
                className={style.modalInput}
                required
              >
                <option value="">Select Shop *</option>
                {shops.map(shop => (
                  <option key={shop.shop_id} value={shop.shop_id}>
                    {shop.shop_name}
                  </option>
                ))}
              </select>
              <textarea
                placeholder="Category Description"
                value={editCategory.category_description}
                onChange={(e) => setEditCategory({...editCategory, category_description: e.target.value})}
                className={style.modalInput}
              />
              <input
                type="file"
                onChange={handleImageChange}
                className={style.modalInput}
                accept="image/*"
              />
              {editCategory.category_image && !imageFile && (
                <img
                  src={resolveImageUrl(editCategory.category_image_url)}
                  alt="Current category"
                  className={style.currentImage}
                  onError={(e) => {
                    e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjYwIiBoZWlnaHQ9IjYwIiBmaWxsPSIjRjVGNUY1Ii8+CjxwYXRoIGQ9Ik0zMCAzNUMzMi43NjE0IDM1IDM1IDMyLjc2MTQgMzUgMzBDMzUgMjcuMjM4NiAzMi43NjE0IDI1IDMwIDI1QzI3LjIzODYgMjUgMjUgMjcuMjM4NiAyNSAzNUMyNSAzMy43NjE0IDI3LjIzODYgMzUgMzAgMzVaIiBmaWxsPSIjQ0NDQ0NDIi8+Cjwvc3ZnPgo='
                  }}
                />
              )}
              <div className={style.modalButtons}>
                <button
                  onClick={() => handleSave(showEditModal)}
                  className={style.saveBtn}
                  disabled={isLoading}
                >
                  {isLoading ? "Saving..." : (showAddModal ? "Add Category" : "Update Category")}
                </button>
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setShowEditModal(false);
                  }}
                  className={style.cancelBtn}
                  disabled={isLoading}
                >
                Cancel
              </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className={style.modalOverlay}>
          <div className={style.modal}>
            <h2>Confirm Deletion</h2>
            <p>Are you sure you want to delete {selectedCategories.length} selected category(ies)?</p>
            <div className={style.modalButtons}>
              <button onClick={confirmDelete} className={style.deleteBtn} disabled={isLoading}>
                {isLoading ? "Deleting..." : "Yes, Delete"}
              </button>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className={style.cancelBtn}
                disabled={isLoading}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoryPage;